import axios from "axios";
import {
  AUTH_REFRESH_URL,
  isUnauthorizedError,
} from "@/utils/authFlow";
import { clearLegacyAuthStorage } from "@/utils/authStorage";

const REFRESH_LOCK_KEY = "vars.auth.refresh.lock";
const REFRESH_RESULT_KEY = "vars.auth.refresh.result";
const REFRESH_LOCK_TIMEOUT_MS = 15000;
const REFRESH_POLL_INTERVAL_MS = 100;

function now() {
  return Date.now();
}

function readJson(storage, key) {
  try {
    return JSON.parse(storage.getItem(key));
  } catch (error) {
    return null;
  }
}

function writeJson(storage, key, value) {
  storage.setItem(key, JSON.stringify(value));
}

function getStorage() {
  if (typeof window === "undefined") return null;
  return window.localStorage || null;
}

function createOwnerId() {
  return `refresh-${now()}-${Math.random().toString(36).slice(2)}`;
}

function delay(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export class AuthRefreshCoordinator {
  constructor(client = axios, storage = getStorage()) {
    this.client = client;
    this.storage = storage;
  }

  async refresh() {
    if (!this.storage) {
      await this.performRefresh();
      return true;
    }

    const owner = createOwnerId();
    const startedAt = now();

    if (this.tryAcquireLock(owner)) {
      try {
        await this.performRefresh();
        this.recordResult("success", owner);
        return true;
      } catch (error) {
        this.recordResult("failure", owner, error);
        throw error;
      } finally {
        this.releaseLock(owner);
      }
    }

    return this.waitForPeerRefresh(startedAt);
  }

  async performRefresh() {
    try {
      await this.client.post(
        AUTH_REFRESH_URL,
        {},
        {
          withCredentials: true,
          skipAuthRefresh: true,
        }
      );
    } catch (error) {
      if (isUnauthorizedError(error)) {
        clearLegacyAuthStorage(this.storage);
      }

      throw error;
    }
  }

  tryAcquireLock(owner) {
    const lock = readJson(this.storage, REFRESH_LOCK_KEY);

    if (lock && lock.expiresAt > now()) return false;

    writeJson(this.storage, REFRESH_LOCK_KEY, {
      owner,
      expiresAt: now() + REFRESH_LOCK_TIMEOUT_MS,
    });

    return readJson(this.storage, REFRESH_LOCK_KEY)?.owner === owner;
  }

  releaseLock(owner) {
    const lock = readJson(this.storage, REFRESH_LOCK_KEY);

    if (lock?.owner === owner) {
      this.storage.removeItem(REFRESH_LOCK_KEY);
    }
  }

  recordResult(status, owner, error = null) {
    writeJson(this.storage, REFRESH_RESULT_KEY, {
      status,
      owner,
      statusCode: error?.response?.status || null,
      at: now(),
    });
  }

  async waitForPeerRefresh(startedAt) {
    const expiresAt = now() + REFRESH_LOCK_TIMEOUT_MS;

    while (now() < expiresAt) {
      const result = readJson(this.storage, REFRESH_RESULT_KEY);

      if (result && result.at >= startedAt) {
        if (result.status === "success") return true;

        const error = new Error("Peer refresh failed");
        if (result.statusCode) {
          error.response = { status: result.statusCode };
        }
        if (isUnauthorizedError(error)) {
          clearLegacyAuthStorage(this.storage);
        }
        throw error;
      }

      const lock = readJson(this.storage, REFRESH_LOCK_KEY);
      if (!lock || lock.expiresAt <= now()) {
        return this.refresh();
      }

      await delay(REFRESH_POLL_INTERVAL_MS);
    }

    throw new Error("Timed out waiting for token refresh");
  }
}

export default new AuthRefreshCoordinator();
